import React from "react";
import type { FC } from "react";
import UtilityPanel from "../layout/UtilityPanel";
import CorePanelFooter from "../layout/CorePanelFooter";
import CoreLayout from "../layout/CoreLayout";
import { IconCheckBlueLg } from "../icons";
import Button from "../form/Button";
import { useAppStore } from "../../store/appStore";
import { endSession } from "../../api/api";
import type { UserAccount } from "../../api/dev-data/getSessionStatus";
import AccountItem from "../components/AccountItem";
import { withErrorBoundary } from "../components/withErrorBoundary";

interface Props {
    utilityResponse: {
        alreadyAuthorizedAccounts: UserAccount[];
        failedToAuthorizeAccounts: UserAccount[];
    };
    getSessionId: () => string | null;
    isEmbedded: boolean;
    onRegOrigin: string;
}

const AuthzComplete: FC<Props> = (props) => {
    const { setSessionStatus } = useAppStore();

    function onFinish() {
        const sessionId = props.getSessionId();
        if (sessionId == null) {
            console.error("Session ID is null");
            return;
        }
        endSession(sessionId, true);
        
        // Send message to parent frame if running in an iframe
        if (props.isEmbedded) {
            window.parent.postMessage({
                status: 'AUTHZ_COMPLETE',
                sessionId: sessionId,
            }, props.onRegOrigin);
        }
        
        setSessionStatus("INITIALIZED");
    }

    function onStartOver() {
        const sessionId = props.getSessionId();
        if (sessionId == null) {
            console.error("Session ID is null");
            return;
        }
        endSession(sessionId, true);
        setSessionStatus("INITIALIZED");
    }

    const header = (
        <div className="self-stretch inline-flex justify-start items-center text-pge-blue gap-3">
            <IconCheckBlueLg />
            <div className="flex-1 justify-start font-normal font-['Inter']">
                <div className="text-base">Authorization complete</div>
            </div>
        </div>
    );

    return (
        <CoreLayout>
            <UtilityPanel
                className="bg-pge-blue text-white"
                headerOverride={header}
            >
                <div className="flex flex-col gap-4 text-black text-xs font-normal font-['Inter']">
                    <div>
                        Thank you for completing the PG&E authorization process.
                    </div>
                    <div>
                        {props.utilityResponse.alreadyAuthorizedAccounts
                            .length > 0 && (
                            <div className="flex flex-col gap-2">
                                The following accounts were already correctly
                                authorized, so we took no action:
                                <ul className="list-disc ml-4">
                                    {props.utilityResponse.alreadyAuthorizedAccounts.map(
                                        (account) => (
                                            <li
                                                key={
                                                    account.utilityAccountNumber
                                                }
                                            >
                                                <AccountItem
                                                    account={account}
                                                />
                                            </li>
                                        )
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </UtilityPanel>
            <CorePanelFooter>
                <Button onClick={onFinish} type="primary">
                    Finish
                </Button>
                <Button onClick={onStartOver} type="primary" subType="outline">
                    Start Over
                </Button>
            </CorePanelFooter>
        </CoreLayout>
    );
};

export default withErrorBoundary(AuthzComplete);
